// Client-side mirror of the server's permission-grant expansion (PermissionSet.From).
// The Roles editor stores/reads CONCRETE tokens (feature:k:read|write, content:type:read|write);
// this expands a role's compact grants (incl. wildcards `*`, `feature:*:write`, `content:*:write`,
// `content:@group:write`) into that concrete set so the checkbox matrix reflects built-in roles
// correctly. Write implies read.
import type { PermissionCatalog } from "./PermissionsContext";

export function expandGrants(catalog: PermissionCatalog, grants: string[]): Set<string> {
	const out = new Set<string>();
	const addFeature = (key: string, action: string) => {
		out.add(`feature:${key}:read`);
		if (action === "write") out.add(`feature:${key}:write`);
	};
	const addContent = (key: string, action: string) => {
		out.add(`content:${key}:read`);
		if (action === "write") out.add(`content:${key}:write`);
	};

	for (const raw of grants ?? []) {
		const g = (raw ?? "").trim();
		if (!g) continue;

		if (g === "*") {
			for (const f of catalog.features) {
				addFeature(f.key, "read");
				if (f.supportsWrite) addFeature(f.key, "write");
			}
			for (const t of catalog.contentTypes) addContent(t.key, "write");
			continue;
		}

		const [resource, sel, action] = g.split(":");
		if (action !== "read" && action !== "write") continue;

		if (resource === "feature") {
			const apply = (f: { key: string; supportsWrite: boolean }) =>
				action === "write" && f.supportsWrite ? addFeature(f.key, "write") : addFeature(f.key, "read");
			if (sel === "*") catalog.features.forEach(apply);
			else {
				const f = catalog.features.find((x) => x.key === sel);
				if (f) apply(f);
			}
		} else if (resource === "content") {
			if (sel === "*") catalog.contentTypes.forEach((t) => addContent(t.key, action));
			else if (sel.startsWith("@")) {
				const grp = catalog.groups.find((x) => x.key === sel.slice(1));
				if (grp) grp.typeKeys.forEach((tk) => addContent(tk, action));
			} else if (catalog.contentTypes.some((t) => t.key === sel)) {
				addContent(sel, action);
			}
		}
	}
	return out;
}

/** Toggle a concrete read/write token in a token set, maintaining write⇒read. */
export function toggleToken(tokens: Set<string>, resource: "feature" | "content", key: string, action: "read" | "write", on: boolean): Set<string> {
	const next = new Set(tokens);
	const read = `${resource}:${key}:read`;
	const write = `${resource}:${key}:write`;
	if (action === "read") {
		if (on) next.add(read);
		else {
			next.delete(read);
			next.delete(write); // can't have write without read
		}
	} else {
		if (on) {
			next.add(write);
			next.add(read);
		} else {
			next.delete(write);
		}
	}
	return next;
}
